import { FileUpload as FileUploadPrimitive } from '@ark-ui/react/file-upload'
import { usePrevious } from '@mantine/hooks'
import { useEffect, useRef, useState } from 'react'

import { cn } from '@/utils'
import { resizeImageToSquare } from '@/utils'

import {
	SmartScrollContainer,
	TSmartScrollContainerHandle,
} from '../SmartScrollContainer'
import { Spinner } from '../Spinner'
import { FileActions } from './FileActions'
import { FileProgress } from './FileProgress'
import { generateVideoThumbnail } from './video.utils'

const FilePreview = ({
	file,
	fileKey,
	index,
	onReplace,
	onDelete,
	deleteButtonLabel,
	showPreviewButton,
	onPreview,
	progress,
	disabled,
}: {
	file: File
	fileKey: string
	index: number
	onReplace: (fileIndex: number) => void
	onDelete: (file: File, index: number) => void
	deleteButtonLabel?: string
	showPreviewButton?: boolean
	onPreview?: () => void
	progress: number
	disabled?: boolean
}) => {
	const [previewImageUrl, setPreviewImageUrl] = useState('')
	const [isProcessing, setIsProcessing] = useState(false)

	useEffect(() => {
		setPreviewImageUrl('')
		setIsProcessing(false)
	}, [file])

	useEffect(() => {
		if (!previewImageUrl) {
			setIsProcessing(true)
			const isVideoFile = file.type.startsWith('video/')

			if (isVideoFile) {
				generateVideoThumbnail(file).then((thumbnailUrl) => {
					setPreviewImageUrl(thumbnailUrl)
					setIsProcessing(false)
				})
			} else if (file.type === 'image/svg+xml') {
				setPreviewImageUrl(URL.createObjectURL(file))
				setIsProcessing(false)
			} else {
				resizeImageToSquare(file, 312)
					.then((blob) => {
						const url = URL.createObjectURL(blob)
						setPreviewImageUrl(url)
						setIsProcessing(false)
					})
					.catch(() => setIsProcessing(false))
			}
		}
	}, [file, previewImageUrl])

	return (
		<FileUploadPrimitive.Item
			key={fileKey}
			file={file}
			className="flex flex-col"
		>
			<div className="relative flex size-30 shrink-0 items-center justify-center rounded-10 border border-border-grey bg-white p-2">
				{previewImageUrl ? (
					<>
						<img
							alt={file.name}
							src={previewImageUrl}
							className="size-full transform-gpu rounded-10 object-cover"
						/>
						{progress < 100 && (
							<div className="absolute inset-0 z-[1] flex flex-col items-center justify-center gap-1 rounded-10 bg-black/50 type-body-caption text-white">
								{progress}%
								<div className="relative h-[8px] w-9 rounded-full bg-white/30">
									<div
										className="absolute left-0 top-0 h-full rounded-full bg-white transition-[width] duration-300 ease-out"
										style={{ width: `${Math.round((36 * progress) / 100)}px` }}
									/>
								</div>
							</div>
						)}
					</>
				) : isProcessing ? (
					<div className="flex size-full items-center justify-center rounded-10 bg-light-blue">
						<Spinner size={40} color="blue" />
					</div>
				) : (
					<FileProgress progress={progress} />
				)}
			</div>
			<FileActions
				showPreviewButton={showPreviewButton}
				onPreview={onPreview}
				onReplace={() => {
					onReplace(index)
				}}
				onDelete={() => onDelete(file, index)}
				deleteButtonLabel={deleteButtonLabel}
				className="mt-2 [&>*]:grow [&>*]:px-0 [&>*]:[flex-basis:0]"
				disabled={disabled}
			/>
		</FileUploadPrimitive.Item>
	)
}

export const MultiFilePreview = ({
	files,
	onReplace,
	onDelete,
	deleteButtonLabel,
	showPreviewButton,
	onPreview,
	progress,
	topMargin,
	disabled,
	getFileKey,
}: {
	files: File[]
	onReplace: (fileIndex: number) => void
	onDelete: (file: File, index: number) => void
	deleteButtonLabel?: string
	showPreviewButton?: boolean
	onPreview?: () => void
	progress?: Record<string, number>
	topMargin: boolean
	disabled?: boolean
	getFileKey: (file: File, index: number) => string
}) => {
	const scrollContainerRef = useRef<TSmartScrollContainerHandle>(null)

	const prevFiles = usePrevious(files)

	useEffect(() => {
		if (files.length > (prevFiles?.length || 0)) {
			scrollContainerRef.current?.scrollToEnd()
		}
	}, [files.length, prevFiles?.length])

	return (
		<SmartScrollContainer ref={scrollContainerRef}>
			<div className={cn('flex gap-4', topMargin && 'mt-4')}>
				{files.map((file, index) => {
					const fileKey = getFileKey(file, index)

					return (
						<FilePreview
							key={fileKey}
							file={file}
							fileKey={fileKey}
							index={index}
							showPreviewButton={showPreviewButton}
							onPreview={onPreview}
							onReplace={onReplace}
							onDelete={onDelete}
							deleteButtonLabel={deleteButtonLabel}
							progress={progress?.[fileKey] ?? 0}
							disabled={disabled}
						/>
					)
				})}
			</div>
		</SmartScrollContainer>
	)
}
